import { Alert, Badge, Icons, Loading } from '@commenda/kakashi';
import { AgentMode, IWorkflowTemplateVersion } from '@commenda/types';
import { ConfirmationModal } from '../../molecules';
import { PanelChat } from '../PanelChat';
import { PanelHeaderActions } from '../PanelHeaderActions';
import { PanelHistoryMenu } from '../PanelHistoryMenu';
import { BuilderContextPill } from './components/BuilderContextPill';
import { ForkBanner } from './components/ForkBanner';
import { TBuilderCanvasBinding, TBuilderOpenTab } from './types/types';
import { useBuilderChatPanel } from './useBuilderChatPanel';

export type TBuilderChatPanelProps = {
  workflowTemplateVersion: IWorkflowTemplateVersion;
  conversationId: string | null;
  canvas: TBuilderCanvasBinding;
  modelId: string;
  onConversationCreated: (conversationId: string) => void;
  onNavigateToVersion: (workflowTemplateVersionId: string) => void;
  onSelectConversation: (conversationId: string) => void;
  onStartNewConversation: () => void;
  onClose: () => void;
  /** Which document tab the host has open. Absent sends no screen context, which is the admin builder. */
  openTab?: TBuilderOpenTab;
};

/** What a workflow chat is usually opened to do. The panel blank state takes its copy per surface
 *  by design, the way the enterprise panel does. */
const BUILDER_EMPTY_COPY = {
  heading: 'Build this workflow by chatting',
  description:
    'Describe the steps you want and they appear on the canvas. You can still drag nodes from the palette at any time.',
  suggestions: [
    'Add a step to this workflow',
    'Walk me through what this workflow does',
    'Add an approval before the filing',
  ],
} as const;

export const BuilderChatPanel = ({
  workflowTemplateVersion,
  conversationId,
  canvas,
  modelId,
  onConversationCreated,
  onNavigateToVersion,
  onSelectConversation,
  onStartNewConversation,
  onClose,
  openTab,
}: TBuilderChatPanelProps) => {
  const panel = useBuilderChatPanel({
    workflowTemplateVersion,
    conversationId,
    canvas,
    modelId,
    onConversationCreated,
    onNavigateToVersion,
    onSelectConversation,
    openTab,
  });
  const { liveVersion, versionActions, targeting } = panel;

  const contextPill = panel.contextLabel ? (
    <BuilderContextPill
      label={panel.contextLabel}
      onRemove={panel.onContextRemove}
    />
  ) : null;

  const composerControls = {
    modeConfig: {
      mode: panel.mode,
      onModeChange: (nextMode: AgentMode) => {
        // The hook has already put a failed fork in the panel's inline error.
        panel.setMode(nextMode).catch(() => undefined);
      },
    },
  };

  return (
    <div className="flex h-full min-w-0 flex-col">
      {versionActions.canDiscard && (
        <ForkBanner
          version={liveVersion.version}
          sourceVersionLabel="the version you opened"
          discardConfig={{
            onDiscard: versionActions.requestDiscard,
            isDisabled: panel.isGenerating,
          }}
        />
      )}

      {panel.inlineError && (
        <div className="px-lg pt-md">
          <Alert variant="destructive" description={panel.inlineError} />
        </div>
      )}

      {Boolean(panel.loadError) && (
        <div className="px-lg pt-md">
          <Alert
            variant="destructive"
            description="Some of this workflow's details could not be loaded. Refresh to try again."
          />
        </div>
      )}

      <div className="relative flex min-h-0 flex-1 flex-col">
        {panel.isLoading ? (
          <div className="flex flex-1 items-center justify-center">
            <Loading />
          </div>
        ) : (
          // The same side-panel chat the enterprise app mounts. Only the send differs, and it has
          // to: a builder conversation cannot exist until the canvas is saved and a target version
          // resolved, which is a sequence this panel owns.
          <PanelChat
            conversationId={conversationId}
            emptyCopy={BUILDER_EMPTY_COPY}
            onStartConversation={async (text, attachments) => {
              const sent = await panel.send(text, attachments, {
                mode: panel.mode,
                modelId,
              });
              // The error is already inline; rejecting keeps the typed text in the composer.
              if (!sent) throw new Error('The builder message was not sent.');
            }}
            headerBadge={
              <Badge variant="gray" size="sm">
                {liveVersion.version}
              </Badge>
            }
            headerTrailing={(access) => (
              <PanelHeaderActions
                access={access}
                onStartNew={onStartNewConversation}
                historyMenu={
                  <PanelHistoryMenu
                    conversations={panel.conversations}
                    activeConversationId={conversationId}
                    onSelect={onSelectConversation}
                    isLoading={panel.isLoading}
                    hasError={Boolean(panel.loadError)}
                  />
                }
                // No expand: the full page is `/agent`, and leaving there would abandon the
                // workflow this panel is attached to.
                onClose={onClose}
              />
            )}
            renderHeaderTitle={() => 'Workflow chat'}
            contextPill={contextPill}
            composerControls={composerControls}
            beforeSend={panel.beforeSend}
            buildSendBody={panel.buildSendBody}
            onGeneratingChange={panel.onGeneratingChange}
          />
        )}
      </div>

      {targeting.isForking && (
        <div className="flex items-center gap-md px-xl py-md">
          <Icons.Loading02 className="size-4 animate-spin text-text-tertiary" />
          <span className="txt-extra-small-regular text-text-tertiary">
            Creating a new version to edit
          </span>
        </div>
      )}

      {versionActions.pendingConfirmation && (
        <ConfirmationModal
          isOpen
          onClose={versionActions.dismissConfirmation}
          tone="destructive"
          title={versionActions.pendingConfirmation.title}
          confirmButtonText={versionActions.pendingConfirmation.confirmLabel}
          onConfirm={versionActions.pendingConfirmation.onConfirm}
          message={
            <ul className="flex flex-col gap-md">
              {versionActions.pendingConfirmation.warnings.map((warning) => (
                <li
                  key={warning}
                  className="txt-small-regular text-text-primary"
                >
                  {warning}
                </li>
              ))}
            </ul>
          }
        />
      )}
    </div>
  );
};
